import {getCopilotSpacePath, SLUG_ERROR_MESSAGE} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import type {CopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useAutoSpaceName} from '../hooks'
import type {CopilotSpaceRepositoryBackedResource} from '../types'
import {resourcesForDuplication} from '../utils/resources-for-duplication'
import {useNavigate} from '@github-ui/use-navigate'
import {Dialog, FormControl, Stack, TextInput, useConfirm} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space'
import {type OwnerItem, SpacesOwnerDropdown} from './SpacesOwnerDropdown'
import styles from './CreateSpaceDialog.module.css'
import {SpaceDialogFooter} from './SpaceDialogFooter'
import {sendEvent} from '@github-ui/hydro-analytics'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'

export function CreateSpaceDialog({
  dialogTitle = 'New Space',
  closeDialog,
  initialSpace,
}: {
  dialogTitle?: string
  closeDialog: () => void
  initialSpace?: CopilotSpace
}) {
  const navigate = useNavigate()

  const nameInputRef = useRef<HTMLInputElement>(null)
  const defaultName = useAutoSpaceName()
  const [name, setName] = useState(initialSpace?.name ?? defaultName)
  const [owner, setOwner] = useState<OwnerItem | null>(null)
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
  const {upsertCopilotSpace, isPending: isSaving} = useUpsertCopilotSpace()
  const confirm = useConfirm()

  const clearErrors = useCallback(
    (fields: string[]) => {
      const updatedErrorMessages = {...errorMessages}
      for (const field of fields) {
        if (updatedErrorMessages[field]) {
          delete updatedErrorMessages[field]
        }
      }
      setErrorMessages(updatedErrorMessages)
    },
    [errorMessages],
  )
  const dropdownOnSelect = useCallback((newOwner: OwnerItem) => setOwner(newOwner), [])

  // Track when the v3 create dialog was opened: https://github.com/github/copilot-experiences/issues/6823
  useEffect(() => {
    sendEvent('copilot_spaces.create_space_v3_dialog_opened', {})
  }, [])

  useEffect(() => {
    if (initialSpace) {
      // If an initial space is being provided we want to focus instead of highlight since the use may not want to
      // change the whole name. They may want to leave it and just set a different owner, for example.
      nameInputRef.current?.focus()
    } else {
      // If the initialSpace is not provided that means we use an auto-generated name. We want to select the input so
      // that the user can quickly set their own name
      nameInputRef.current?.select()
    }
  }, [initialSpace])

  const saveSpace = async (): Promise<void> => {
    if (isSaving) return

    let resourcesToSave = initialSpace?.resources ? [...initialSpace.resources] : []
    let resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[] = []

    // If duplicating a space
    if (initialSpace && owner) {
      const duplicationResult = resourcesForDuplication(initialSpace.resources || [], owner)
      resourcesToSave = duplicationResult.resourcesToSave
      resourcesThatWillNotTransfer = duplicationResult.resourcesThatWillNotTransfer
    }

    // Treat the save as confirmed unless some resource will not transfer
    let confirmed = true
    if (resourcesThatWillNotTransfer.length > 0) {
      // Returns a comma separated list of owner logins from resources that will not transfer. E.g. "github, vercel, and primer"
      const ownerLoginsAsSentence = new Intl.ListFormat().format(
        // Make it a Set first so that owner logins are unique. Then convert back to Array for formatting
        Array.from(
          new Set(
            resourcesThatWillNotTransfer.map(r => r.ownerLogin).filter((login): login is string => login !== undefined),
          ),
        ),
      )
      const content = `Private resources owned by <strong>${ownerLoginsAsSentence}</strong> will not be duplicated to the new space.`
      confirmed = await confirm({
        title: 'Some resources will not be duplicated',
        // Allows the <strong> tags to be rendered as HTML but will still escape unsafe HTML if present.
        content: <UnsafeHTMLText html={content} />,
        cancelButtonContent: 'Never mind',
        confirmButtonContent: 'Continue',
        confirmButtonType: 'primary',
      })
    }

    if (!confirmed) return

    try {
      const newSpace = await upsertCopilotSpace({
        name,
        ownerId: owner?.id,
        ownerType: owner?.type,
        resources: resourcesToSave,
        generalInstructions: initialSpace?.generalInstructions,
      })

      setErrorMessages({})
      // First close the dialog to prevent conditions where we navigate before the dialog is closed and the dialog pops
      // open again when going back to the space's page.
      closeDialog()
      // navigate to the newly created space
      const path = getCopilotSpacePath(newSpace)
      return navigate(path)
    } catch (errors) {
      const errs = errors ?? {}
      setErrorMessages(errs as Record<string, string>)
    }
  }
  const handleCancel = () => {
    closeDialog()
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title={dialogTitle}
      onClose={closeDialog}
      width="large"
      aria-label={dialogTitle}
      className={styles.createSpaceDialog}
      renderFooter={() => (
        <SpaceDialogFooter
          onCancel={handleCancel}
          onSave={saveSpace}
          isLoading={isSaving}
          disabled={!owner}
          saveButtonText="Create Space"
        />
      )}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()
          e.stopPropagation()
          await saveSpace()
        }}
      >
        <Stack>
          <FormControl>
            <FormControl.Label required>Space name</FormControl.Label>
            <TextInput
              ref={nameInputRef}
              autoFocus
              block
              data-testid="copilot-space-name-input"
              name="copilot-space-name"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors(['name', 'slug'])
              }}
            />
            <FormControl.Caption>Choose a name that describes your project or use case</FormControl.Caption>
            {errorMessages.name ? (
              <FormControl.Validation variant="error">
                <span>Name {errorMessages.name}</span>
              </FormControl.Validation>
            ) : errorMessages.slug ? (
              <FormControl.Validation variant="error">
                <span>{SLUG_ERROR_MESSAGE}</span>
              </FormControl.Validation>
            ) : null}
          </FormControl>
          <SpacesOwnerDropdown onSelect={dropdownOnSelect} />
        </Stack>
      </form>
    </Dialog>
  )
}

try{ CreateSpaceDialog.displayName ||= 'CreateSpaceDialog' } catch {}